import React, { useMemo, useRef, useState, useCallback } from 'react';
import { Loader2, Waypoints } from 'lucide-react';

// =============================================================================
// LaneNetworkMap
//
// The rating pages' half of the picture Planning › Network draws. Same idea,
// different question: planning asks "can every site be supplied", rating asks
// "is every lane priced, and by whom". So the node badge is money and carriers
// rather than balance, and there is no drawing tool — you cannot invent a lane
// by dragging on a rating screen, because a lane is a planning decision.
//
// Deliberately GENERIC over both modules, because their graphs are not the same
// shape:
//
//   INTER-DEPOT   site → site. A true network; both ends are depots.
//   SECONDARY     site → delivery zone. A star out of each depot; the far end
//                 is a geography, not a place you can dispatch from.
//
// Which is why this takes nodes and edges rather than sites and lanes. The
// caller decides what a node is.
//
// LAYOUT IS EPHEMERAL HERE. Planning persists positions (bud_site_positions)
// because the planner arranges the network as a working artefact. On a rating
// page the map is a way in to a lane, so it auto-lays-out every time and drag
// is a nudge, not a document. No save button, nothing to forget to press.
// =============================================================================

const NODE_W = 168;
const NODE_H = 70;
const TIER_X = 250;
const ROW_Y = 112;
const PAD = 60;

// One shared vocabulary for "is this lane alright", so inter-depot and
// secondary cannot drift into meaning different things by the same colour.
export const EDGE_TONE = {
  ok: { stroke: '#64748b', dash: undefined },
  priced: { stroke: '#059669', dash: undefined },
  bad: { stroke: '#dc2626', dash: undefined },
  empty: { stroke: '#cbd5e1', dash: '5 4' },
};

const fmt = (n) => Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: 0 });

// Nodes without a position get one: tier across, index down. Tier is the
// caller's business — a depot is tier 0 and its zones tier 1 on secondary,
// while inter-depot uses the site master's own tier.
const autoLayout = (nodes) => {
  const perTier = {};
  return nodes.map((n) => {
    const tier = n.tier ?? 9;
    perTier[tier] = perTier[tier] || 0;
    const row = perTier[tier]++;
    return {
      ...n,
      x: n.x ?? PAD + tier * TIER_X,
      y: n.y ?? PAD + row * ROW_Y,
    };
  });
};

export default function LaneNetworkMap({
  nodes: rawNodes,
  edges: rawEdges,
  selectedEdgeId = null,
  onSelectEdge,
  loading = false,
  emptyTitle = 'Nothing on the network yet.',
  emptyHint = '',
  legend = null,
  inspector = null,
}) {
  const svgRef = useRef(null);
  const dragRef = useRef(null);
  const [offsets, setOffsets] = useState({});   // node id -> { dx, dy }

  const nodes = useMemo(() => autoLayout(rawNodes || []).map((n) => {
    const o = offsets[n.id];
    return o ? { ...n, x: n.x + o.dx, y: n.y + o.dy } : n;
  }), [rawNodes, offsets]);

  const nodeById = useMemo(() => Object.fromEntries(nodes.map((n) => [n.id, n])), [nodes]);

  const extent = useMemo(() => {
    const maxX = nodes.reduce((a, n) => Math.max(a, n.x + NODE_W), 0);
    const maxY = nodes.reduce((a, n) => Math.max(a, n.y + NODE_H), 0);
    return { w: Math.max(1000, maxX + PAD), h: Math.max(500, maxY + PAD) };
  }, [nodes]);

  const toSvg = useCallback((evt) => {
    const svg = svgRef.current;
    if (!svg) return { x: 0, y: 0 };
    const pt = svg.createSVGPoint();
    pt.x = evt.clientX;
    pt.y = evt.clientY;
    const ctm = svg.getScreenCTM();
    if (!ctm) return { x: 0, y: 0 };
    const p = pt.matrixTransform(ctm.inverse());
    return { x: p.x, y: p.y };
  }, []);

  const onNodePointerDown = (node) => (evt) => {
    evt.stopPropagation();
    const p = toSvg(evt);
    const cur = offsets[node.id] || { dx: 0, dy: 0 };
    dragRef.current = { id: node.id, startX: p.x, startY: p.y, baseDx: cur.dx, baseDy: cur.dy };
    evt.currentTarget.setPointerCapture?.(evt.pointerId);
  };

  const onPointerMove = (evt) => {
    const d = dragRef.current;
    if (!d) return;
    const p = toSvg(evt);
    setOffsets((prev) => ({
      ...prev,
      [d.id]: { dx: d.baseDx + (p.x - d.startX), dy: d.baseDy + (p.y - d.startY) },
    }));
  };

  const onPointerUp = () => { dragRef.current = null; };

  // Edges bow to one side so an out-and-back pair draws as two visible arcs
  // rather than one line with two arrowheads. The direction chevron sits at the
  // true curve midpoint — for a quadratic Bezier that is 0.25*P0 + 0.5*C +
  // 0.25*P2, and the tangent there is simply P2 - P0.
  const edges = useMemo(() => (rawEdges || []).map((e) => {
    const a = nodeById[e.from];
    const b = nodeById[e.to];
    if (!a || !b) return null;
    const x1 = a.x + NODE_W / 2;
    const y1 = a.y + NODE_H / 2;
    const x2 = b.x + NODE_W / 2;
    const y2 = b.y + NODE_H / 2;
    const dx = x2 - x1;
    const dy = y2 - y1;
    const len = Math.hypot(dx, dy) || 1;
    const bow = 28;
    const cx = (x1 + x2) / 2 - (dy / len) * bow;
    const cy = (y1 + y2) / 2 + (dx / len) * bow;
    return {
      edge: e,
      midX: 0.25 * x1 + 0.5 * cx + 0.25 * x2,
      midY: 0.25 * y1 + 0.5 * cy + 0.25 * y2,
      angle: (Math.atan2(dy, dx) * 180) / Math.PI,
      d: `M ${x1} ${y1} Q ${cx} ${cy} ${x2} ${y2}`,
    };
  }).filter(Boolean), [rawEdges, nodeById]);

  return (
    <div className="flex-1 min-h-0 flex gap-4">
      <div className="flex-1 min-w-0 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 overflow-auto relative">
        {loading ? (
          <div className="absolute inset-0 flex items-center justify-center">
            <Loader2 className="w-5 h-5 animate-spin text-slate-400" />
          </div>
        ) : nodes.length === 0 ? (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 text-center px-6">
            <Waypoints className="w-8 h-8 text-slate-300 dark:text-slate-600" />
            <p className="m-0 text-sm text-slate-500 dark:text-slate-400">{emptyTitle}</p>
            {emptyHint && <p className="m-0 text-xs text-slate-400 max-w-sm">{emptyHint}</p>}
          </div>
        ) : (
          <svg
            ref={svgRef}
            viewBox={`0 0 ${extent.w} ${extent.h}`}
            className="w-full"
            style={{ minHeight: '100%', touchAction: 'none' }}
            onPointerMove={onPointerMove}
            onPointerUp={onPointerUp}
            onPointerLeave={onPointerUp}
            onClick={() => onSelectEdge?.(null)}
          >
            <defs>
              {['#94a3b8', '#2563eb', '#059669', '#dc2626'].map((c, i) => (
                <marker key={c} id={`lnm-arrow${i}`} viewBox="0 0 10 10" refX="9" refY="5"
                        markerWidth="6" markerHeight="6" orient="auto-start-reverse">
                  <path d="M 0 0 L 10 5 L 0 10 z" fill={c} />
                </marker>
              ))}
            </defs>

            {edges.map(({ edge, midX, midY, angle, d }) => {
              const sel = selectedEdgeId === edge.id;
              const tone = EDGE_TONE[edge.tone] || EDGE_TONE.ok;
              const colour = sel ? '#2563eb' : tone.stroke;
              const markerId = sel ? 1 : edge.tone === 'priced' ? 2 : edge.tone === 'bad' ? 3 : 0;
              return (
                <g key={edge.id} className="cursor-pointer"
                   onClick={(ev) => { ev.stopPropagation(); onSelectEdge?.(edge); }}>
                  {/* Fat invisible hit area — a 2px line is unclickable */}
                  <path d={d} stroke="transparent" strokeWidth="20" fill="none" />
                  <path d={d} stroke={colour} strokeWidth={sel ? 2.5 : 2}
                        strokeDasharray={tone.dash} fill="none"
                        markerEnd={`url(#lnm-arrow${markerId})`} />
                  <g transform={`translate(${midX}, ${midY}) rotate(${angle})`} pointerEvents="none">
                    <path d="M -5 -5 L 5 0 L -5 5 Z" fill={colour} />
                  </g>
                  {edge.label && (
                    <g transform={`translate(${midX}, ${midY - 17})`} pointerEvents="none">
                      <rect x={-Math.max(30, edge.label.length * 3.4)} y="-10"
                            width={Math.max(60, edge.label.length * 6.8)} height="20" rx="10"
                            fill={sel ? '#2563eb' : '#f8fafc'} stroke={sel ? '#2563eb' : '#e2e8f0'} />
                      <text textAnchor="middle" y="4" fontSize="10.5" fontWeight="600"
                            fill={sel ? '#ffffff' : '#475569'}>{edge.label}</text>
                    </g>
                  )}
                </g>
              );
            })}

            {nodes.map((n) => (
              <g key={n.id} transform={`translate(${n.x}, ${n.y})`}
                 onPointerDown={onNodePointerDown(n)}
                 style={{ cursor: 'grab' }}>
                <rect width={NODE_W} height={NODE_H} rx="10" fill="#ffffff"
                      stroke={n.alert ? '#dc2626' : '#e2e8f0'} strokeWidth={n.alert ? 2 : 1} />
                <rect width="5" height={NODE_H} rx="2.5" fill={n.colour || '#64748b'} />
                <text x="14" y="20" fontSize="12" fontWeight="700" fill="#0f172a">
                  {n.label.length > 21 ? `${n.label.slice(0, 20)}…` : n.label}
                </text>
                <text x="14" y="34" fontSize="10" fill="#94a3b8">{n.sublabel || ''}</text>
                {n.metric && (
                  <text x="14" y="52" fontSize="10" fontWeight="600"
                        fill={n.alert ? '#dc2626' : '#475569'}>{n.metric}</text>
                )}
              </g>
            ))}
          </svg>
        )}
      </div>

      <div className="w-72 shrink-0 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 p-4 overflow-y-auto">
        {inspector || legend}
      </div>
    </div>
  );
}

export { fmt as fmtNetwork };
